import { cn } from '@/lib/utils';
import './dippy-animations.css';

interface DippyCharacterProps {
  avatar: 'seedling' | 'robot';
  expression: 'idle' | 'alert' | 'thinking';
  badgeCount?: number;
  onClick: () => void;
}

export default function DippyCharacter({ avatar, expression, badgeCount = 0, onClick }: DippyCharacterProps) {
  const animClass = expression === 'idle' ? 'dippy-idle' : expression === 'alert' ? 'dippy-alert' : 'dippy-thinking';

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-16 h-16 rounded-full bg-white border-2 border-green-300 shadow-lg cursor-pointer',
        'hover:shadow-xl hover:border-green-400 transition-shadow',
        'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
        animClass
      )}
      aria-label="Open Dippy assistant"
      data-testid="dippy-character"
    >
      <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center">
        {avatar === 'seedling' ? (
          <SeedlingAvatar expression={expression} />
        ) : (
          <RobotAvatar expression={expression} />
        )}
      </div>

      {badgeCount > 0 && (
        <span
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
          data-testid="dippy-badge"
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
    </button>
  );
}

function SeedlingAvatar({ expression }: { expression: string }) {
  const eyeScale = expression === 'alert' ? 1.3 : 1;
  const mouthD = expression === 'thinking' ? 'M18,34 Q22,33 26,34' : 'M18,34 Q22,38 26,34';

  return (
    <svg viewBox="0 0 44 44" className="w-12 h-12">
      {/* Pot */}
      <path d="M12,38 L14,44 L30,44 L32,38 Z" fill="#8B6914" />
      <rect x="10" y="35" width="24" height="4" rx="1" fill="#A0781E" />

      {/* Stem */}
      <path d="M22,35 Q22,28 22,24" stroke="#4CAF50" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      {/* Leaves */}
      <ellipse cx="17" cy="22" rx="5" ry="3" fill="#66BB6A" transform="rotate(-30 17 22)" />
      <ellipse cx="27" cy="20" rx="5" ry="3" fill="#66BB6A" transform="rotate(20 27 20)" />

      {/* Sprout top */}
      <ellipse cx="22" cy="16" rx="4" ry="5" fill="#81C784" />

      {/* Face */}
      <circle cx="19" cy="28" r={1.5 * eyeScale} fill="#333" />
      <circle cx="25" cy="28" r={1.5 * eyeScale} fill="#333" />
      {/* Eye shine */}
      <circle cx="19.5" cy="27.5" r={0.5 * eyeScale} fill="white" />
      <circle cx="25.5" cy="27.5" r={0.5 * eyeScale} fill="white" />
      {/* Cheeks */}
      <circle cx="16" cy="31" r="2" fill="#FFB6C1" opacity="0.5" />
      <circle cx="28" cy="31" r="2" fill="#FFB6C1" opacity="0.5" />
      {/* Mouth */}
      <path d={mouthD} stroke="#333" strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function RobotAvatar({ expression }: { expression: string }) {
  const eyeH = expression === 'alert' ? 5 : 3;
  const antennaY = expression === 'thinking' ? 4 : 6;

  return (
    <svg viewBox="0 0 44 44" className="w-12 h-12">
      {/* Antenna */}
      <line x1="22" y1={antennaY + 2} x2="22" y2="14" stroke="#9E9E9E" strokeWidth="2" />
      <circle cx="22" cy={antennaY} r="3" fill="#4CAF50" />

      {/* Head */}
      <rect x="8" y="14" width="28" height="22" rx="6" fill="#78909C" />
      <rect x="10" y="16" width="24" height="18" rx="4" fill="#90A4AE" />

      {/* Eyes */}
      <rect x="14" y="22" width="5" height={eyeH} rx="1" fill="#4FC3F7" />
      <rect x="25" y="22" width="5" height={eyeH} rx="1" fill="#4FC3F7" />
      {/* Eye shine */}
      <rect x="17" y="22" width="1.5" height="1.5" rx="0.5" fill="white" opacity="0.7" />
      <rect x="28" y="22" width="1.5" height="1.5" rx="0.5" fill="white" opacity="0.7" />

      {/* Mouth */}
      <rect x="16" y="30" width="12" height="2" rx="1" fill="#546E7A" />

      {/* Ears */}
      <rect x="4" y="20" width="4" height="8" rx="2" fill="#78909C" />
      <rect x="36" y="20" width="4" height="8" rx="2" fill="#78909C" />

      {/* Body hint */}
      <rect x="14" y="36" width="16" height="6" rx="3" fill="#78909C" />
    </svg>
  );
}
